<script lang="ts">
	import { applyAction, enhance } from '$app/forms';
	import { goto } from '$app/navigation';
	import { page } from '$app/state';
	import {
		Card,
		SearchDate,
		SearchFrom,
		SearchPassengers,
		SearchTime,
		SearchTo
	} from '$lib/components';
	import {
		searchFrom,
		searchFromPlaceId,
		searchTo,
		searchToPlaceId
	} from '$lib/stores/search.svelte';
	import { Loader, MoveHorizontal } from 'lucide-svelte';
	import { sleep } from '../../utils/sleep';

	type Props = {
		from?: string;
		to?: string;
		title?: string;
		apiKey: string;
	};

	let { title = 'Where would you like to go?', apiKey, ...cities }: Props = $props();
	let searching = $state(false);

	if (cities.from) {
		searchFrom.current = cities.from;
	}

	if (cities.to) {
		searchTo.current = cities.to;
	}

	async function setErrorMessage(message: string[]) {
		const e = message[0];
		if (e.includes('client:')) {
			errorMessage = e.replace('client:', '');
		} else {
			errorMessage = 'We do not provide this type of service';
		}
		await sleep(7000);
		errorMessage = undefined;
	}

	function swapCities() {
		const from = searchFrom.current;
		const fromPlaceId = searchFromPlaceId.current;
		const to = searchTo.current;
		const toPlaceId = searchToPlaceId.current;
		searchFrom.current = to;
		searchFromPlaceId.current = toPlaceId;
		searchTo.current = from;
		searchToPlaceId.current = fromPlaceId;
	}

	let errorMessage = $state<string | undefined>(undefined);
	$effect(() => {
		if (page.form && page.form.message) {
			setErrorMessage(page.form.message);
		}
	});
</script>

<div class="w-full md:w-[500px] xl:w-full">
	<Card>
		<div class="pl-1 pb-2">
			<div class="flex items-center gap-x-3">
				<p class="~text-2xl/3xl mb-2 font-bold">{title}</p>
				<hr class="hidden lg:block border-2 border-primary flex-1" />
			</div>
			<p class="text-slate-600 dark:text-slate-400">
				Book your next trip with us today and experience the difference.
			</p>
		</div>
		<form
			method="POST"
			action="/search"
			class="grid grid-cols-2 xl:grid-cols-8 gap-3 xl:gap-x-2"
			use:enhance={() => {
				searching = true;
				return async ({ result }) => {
					if (result.type === 'success' && result.data?.quoteId) {
						goto(`/search/${result.data.quoteId}#cars`);
					}
					searching = false;
					applyAction(result);
				};
			}}
		>
			<div class="col-span-2 relative">
				<SearchFrom error={page.form?.from} {apiKey} />
				<div class="absolute rotate-90 xl:rotate-0 right-3 xl:-right-3 top-14 xl:top-5 z-10">
					<button
						type="button"
						aria-label="Swap Pickup and Drop Off Locations"
						class="p-1 xl:p-0.5 bg-one rounded-full border border-slate-600"
						onclick={() => swapCities()}
					>
						<MoveHorizontal class="text-primary" size="16" />
					</button>
				</div>
			</div>
			<div class="col-span-2">
				<SearchTo error={page.form?.to} {apiKey} />
			</div>
			<div>
				<SearchDate minDate={new Date()} error={page.form?.date} />
			</div>
			<div>
				<SearchTime error={page.form?.time} />
			</div>
			<div class="col-span-2 xl:col-span-1">
				<SearchPassengers error={page.form?.adults || page.form?.children || page.form?.infants} />
			</div>
			<div class="col-span-2 xl:col-span-1">
				<button
					type="submit"
					disabled={searching}
					class="border-slate-300 dark:border-slate-700 from-two to-one text-primary hover:border-primary py-2 xl:py-0 xl:min-h-full w-full rounded-md border bg-gradient-to-r text-xl uppercase"
				>
					{#if !searching}
						Search
					{:else}
						<Loader class="mx-auto animate-spin" />
					{/if}
				</button>
			</div>
		</form>
		{#if errorMessage && errorMessage !== ''}
			<p class="pt-1 text-danger text-sm">{errorMessage}</p>
		{:else}
			<p class="pt-1 italic text-slate-500 dark:text-slate-400 text-sm">
				Free cancellation 24 hours before departure. Door to door service.
			</p>
		{/if}
	</Card>
</div>
